import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import React, { useEffect, useRef, useState } from "react";
import ShadowCard from "../../components/common/ShadowCard";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { ErrorToast, SuccessToast } from "../../components/toast/Toasts";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import {
  findAllOrderLabelSplit,
  getDataByUniqueProductID,
  insertOrderLabelSplit,
} from "./services/splitItem.services";
import { clearSplitItemState } from "./splitItemSlice";
import SplitItemArray from "./SplitItemArray";
import { findAllActiveItem } from "../item/services/item.services";
import { formatWeight } from "../../_helper/helper";

const SplitChangeWatcher = ({
  formRenderProps,
}: {
  formRenderProps: FormRenderProps;
}) => {
  const readyData = formRenderProps.valueGetter("ReadyData") || [];
  const ORDERLabeling = formRenderProps.valueGetter("ORDERLabeling") || [];
  useEffect(() => {
    ORDERLabeling.forEach((item: any, index: number) => {
      const itemId = item.ItemID || item.id;
      const splitWeightSum = readyData.reduce((sum: number, readyItem: any) => {
        const splitItem = (readyItem.ReadyDataChild || []).find(
          (child: any) => {
            const childId = child.ItemID || child.id;
            return Number(childId) === Number(itemId);
          },
        );
        return sum + (splitItem ? Number(splitItem.Weight || 0) : 0);
      }, 0);
      const splitPcsSum = readyData.reduce((sum: number, readyItem: any) => {
        const splitItem = (readyItem.ReadyDataChild || []).find(
          (child: any) => {
            const childId = child.ItemID || child.id;
            return Number(childId) === Number(itemId);
          },
        );
        return sum + (splitItem ? Number(splitItem.Pcs || 0) : 0);
      }, 0);

      formRenderProps.onChange(`ORDERLabeling.${index}.pendingWt`, {
        value: Number(item.Weight || 0) - splitWeightSum,
      });
      formRenderProps.onChange(`ORDERLabeling.${index}.pendingP`, {
        value: Number(item.Pcs || 0) - splitPcsSum,
      });
    });
  }, [
    readyData
      ?.map((item: any) =>
        (item.ReadyDataChild || [])
          ?.map(
            (child: any) =>
              `${child.ItemID || child.id}_${child.Weight || 0}_${child.Pcs || 0}`,
          )
          .join("|"),
      )
      .join(","),
  ]);
  return null;
};

const SplitItem: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryId = searchParams.get("id");
  const gridRef = useRef<any>(null);

  // Redux State
  const loading = useAppSelector((state) => state.splitItem.loading);
  const dropdownList =
    useAppSelector((state) => state.splitItem.labelSplitDropdownList) || [];
  const mainLabelData = useAppSelector(
    (state) => state.splitItem.mainLabelData,
  );

  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [formKey, setFormKey] = useState(1);

  // Fetch initial dropdown and item lists
  useEffect(() => {
    dispatch(findAllOrderLabelSplit());
    dispatch(findAllActiveItem());
    return () => {
      dispatch(clearSplitItemState());
    };
  }, [dispatch]);

  // Handle route query param pre-select
  useEffect(() => {
    if (queryId) {
      setSelectedLabel(queryId);
      dispatch(getDataByUniqueProductID({ UniqueProductID: queryId }));
    }
  }, [queryId, dispatch]);

  // Reset form when main label data is fetched
  useEffect(() => {
    if (mainLabelData) {
      setFormKey((prev) => prev + 1);
    }
  }, [mainLabelData]);

  const handleView = () => {
    if (selectedLabel) {
      dispatch(getDataByUniqueProductID({ UniqueProductID: selectedLabel }));
    } else {
      ErrorToast("Please select a label to split");
    }
  };

  const handleFormClose = () => {
    navigate("/splititem");
  };

  const handleSubmit = async (values: any) => {
    if (!mainLabelData) {
      ErrorToast("No label selected");
      return;
    }

    const mainGross = Number(mainLabelData.GrossWeight || 0);
    const mainLess = Number(mainLabelData.LessWeight || 0);
    const mainNet = Number(mainLabelData.NetWeight || 0);

    const splits = values.ReadyData || [];
    if (splits.length === 0) {
      ErrorToast("Please add at least one split item");
      return;
    }

    let totalSplitGross = 0;
    let totalSplitLess = 0;
    let totalSplitNet = 0;

    const itemTotals: { [key: number]: { weight: number; pcs: number } } = {};

    for (let i = 0; i < splits.length; i++) {
      const split = splits[i];
      if (!String(split.ProductID || "").trim()) {
        ErrorToast(`Please Select Item for Split #${i + 1}`);
        return;
      }
      if (Number(split.GrossWeight || 0) <= 0) {
        ErrorToast(`Please Enter a Valid Gross Weight for Split #${i + 1}`);
        return;
      }
      if (!split.ReadyDataChild || split.ReadyDataChild.length === 0) {
        ErrorToast(`Please Add at Least One Child Item to Split #${i + 1}`);
        return;
      }

      totalSplitGross += Number(split.GrossWeight || 0);
      totalSplitLess += Number(split.LessWeight || 0);
      totalSplitNet += Number(split.NetWeight || 0);

      split.ReadyDataChild.forEach((child: any) => {
        const id = Number(child.ItemID);
        if (id) {
          if (!itemTotals[id]) {
            itemTotals[id] = { weight: 0, pcs: 0 };
          }
          itemTotals[id].weight += Number(child.Weight || 0);
          itemTotals[id].pcs += Number(child.Pcs || 0);
        }
      });
    }

    const epsilon = 0.001;

    if (Math.abs(totalSplitGross - mainGross) > epsilon) {
      ErrorToast(
        `Total Gross Weight of splits (${totalSplitGross.toFixed(3)}) does not match main label Gross Weight (${mainGross.toFixed(3)}).`,
      );
      return;
    }
    if (Math.abs(totalSplitLess - mainLess) > epsilon) {
      ErrorToast(
        `Total Less Weight of splits (${totalSplitLess.toFixed(3)}) does not match main label Less Weight (${mainLess.toFixed(3)}).`,
      );
      return;
    }
    if (Math.abs(totalSplitNet - mainNet) > epsilon) {
      ErrorToast(
        `Total Net Weight of splits (${totalSplitNet.toFixed(3)}) does not match main label Net Weight (${mainNet.toFixed(3)}).`,
      );
      return;
    }

    const mainItemsMap: {
      [key: number]: { weight: number; pcs: number; name: string };
    } = {};
    const mainItemsList = mainLabelData.ORDERLabeling || [];

    mainItemsList.forEach((item: any) => {
      const id = item.ItemID || item.item_id || item.id;
      if (id) {
        if (!mainItemsMap[id]) {
          mainItemsMap[id] = {
            weight: 0,
            pcs: 0,
            name: item.ItemName || "Unknown",
          };
        }
        mainItemsMap[id].weight += Number(item.Weight || 0);
        mainItemsMap[id].pcs += Number(item.Pcs || 0);
      }
    });

    for (const idStr in mainItemsMap) {
      const id = Number(idStr);
      const mainItem = mainItemsMap[id];
      const splitItem = itemTotals[id] || { weight: 0, pcs: 0 };

      if (Math.abs(splitItem.weight - mainItem.weight) > epsilon) {
        ErrorToast(
          `Total weight for item "${mainItem.name}" (${splitItem.weight.toFixed(3)}) does not match main label weight (${mainItem.weight.toFixed(3)}).`,
        );
        return;
      }
      if (splitItem.pcs !== mainItem.pcs) {
        ErrorToast(
          `Total Pcs for item "${mainItem.name}" (${splitItem.pcs}) does not match main label Pcs (${mainItem.pcs}).`,
        );
        return;
      }
    }

    const payload = {
      ReadyData: splits.map((split: any) => ({
        ProductID: Number(split.ProductID),
        UniqueProductID: mainLabelData.UniqueProductID,
        GrossWeight: Number(split.GrossWeight || 0),
        LessWeight: Number(split.LessWeight || 0),
        NetWeight: Number(split.NetWeight || 0),
        IsActive: true,
        ReadyDataChild: split.ReadyDataChild.map((child: any) => ({
          ItemID: Number(child.ItemID),
          Weight: Number(child.Weight || 0),
          Pcs: Number(child.Pcs || 0),
          CustomerRate: Number(child.CustomerRate || 0),
          Total: Number(child.Total || 0),
        })),
      })),
    };

    try {
      const response = await dispatch(insertOrderLabelSplit(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        SuccessToast("Split Item Label saved successfully!");
        navigate("/splititem");
      }
    } catch (err) {
      console.error("Error in split save:", err);
      ErrorToast("Failed to save split items");
    }
  };

  const initialValues = {
    SelectedProductID: selectedLabel,
    MainGrossWeight: mainLabelData?.GrossWeight || 0,
    MainLessWeight: mainLabelData?.LessWeight || 0,
    MainNetWeight: mainLabelData?.NetWeight || 0,
    ORDERLabeling: mainLabelData?.ORDERLabeling || [],
    ReadyData: [
      {
        ProductID: "",
        GrossWeight: 0,
        LessWeight: 0,
        NetWeight: 0,
        ReadyDataChild: [],
      },
    ],
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}

      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <SplitChangeWatcher formRenderProps={formRenderProps} />
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "24.6%" },
                  { width: "24.6%" },
                  { width: "24.6%" },
                  { width: "24.6%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <Typography.h4>Split Item</Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    display: "flex",
                    marginTop: "22px",
                    justifyContent: "end",
                  }}
                >
                  Select Item to Split:
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="SelectedProductID"
                    component={FormSelectionField}
                    placeholder="Select Item to Split"
                    options={dropdownList.map((item: any) => {
                      return {
                        value: item.UniqueProductID,
                        label: `${item.item_name} - ${item.UniqueProductID?.toUpperCase()}`,
                      };
                    })}
                    onChange={(e: any) => {
                      setSelectedLabel(e.value);
                    }}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ marginTop: 20 }}>
                  <Button
                    type="button"
                    fillMode="solid"
                    themeColor="primary"
                    onClick={handleView}
                    disabled={!selectedLabel}
                  >
                    View
                  </Button>
                </GridLayoutItem>

                {mainLabelData && (
                  <>
                    <GridLayoutItem
                      style={{
                        display: "flex",
                        marginTop: "22px",
                        justifyContent: "end",
                      }}
                    >
                      Gross Weight (gm):
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="MainGrossWeight"
                        placeholder="0.000"
                        format="n3"
                        component={FormNumericTextField}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}></GridLayoutItem>

                    <GridLayoutItem
                      style={{
                        display: "flex",
                        marginTop: "22px",
                        justifyContent: "end",
                      }}
                    >
                      Less Weight (gm):
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="MainLessWeight"
                        placeholder="0.000"
                        format="n3"
                        component={FormNumericTextField}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}></GridLayoutItem>

                    <GridLayoutItem
                      style={{
                        display: "flex",
                        marginTop: "22px",
                        justifyContent: "end",
                      }}
                    >
                      Net Weight (gm):
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="MainNetWeight"
                        placeholder="0.000"
                        format="n3"
                        component={FormNumericTextField}
                        disabled={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}></GridLayoutItem>
                  </>
                )}
              </GridLayout>
              {/* </ShadowCard> */}

              {/* First Section Details Table */}
              {mainLabelData && (
                // <ShadowCard style={{ marginTop: 10, padding: 12 }}>
                <>
                  <Typography.h5
                    style={{ marginBottom: 10, fontWeight: "bold" }}
                  >
                    Items Detail
                  </Typography.h5>
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered table-hover field-array-table">
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Weight (gm)</th>
                          <th>Remaining Weight (gm)</th>
                          <th>Pcs</th>
                          <th>Remaining Pcs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(
                          formRenderProps.valueGetter("ORDERLabeling") || []
                        ).map((item: any, idx: number) => {
                          return (
                            <tr key={idx}>
                              <td>{item.ItemName || "Unknown"}</td>
                              <td>{formatWeight(item.Weight)}</td>
                              <td
                                style={{
                                  fontWeight: "bold",
                                  color:
                                    item?.pendingWt === 0 ? "red" : "green",
                                }}
                              >
                                {typeof item?.pendingWt === "number"
                                  ? formatWeight(item.pendingWt)
                                  : formatWeight(item.Weight)}
                              </td>
                              <td>{item.Pcs || 0}</td>

                              <td
                                style={{
                                  fontWeight: "bold",
                                  color: item?.pendingP === 0 ? "red" : "green",
                                }}
                              >
                                {typeof item?.pendingP === "number"
                                  ? item.pendingP
                                  : item.Pcs || 0}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </ShadowCard>

            {/* Second Section: Split Items List managed as FieldArray */}
            {mainLabelData && (
              <ShadowCard style={{ marginTop: 10 }}>
                <FieldArray
                  formRenderProps={formRenderProps}
                  component={SplitItemArray}
                  name="ReadyData"
                />

                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[{ width: "50%" }, { width: "50%" }]}
                >
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      textAlign: "end",
                      marginTop: "20px",
                      width: "100%",
                    }}
                  >
                    <Button
                      type="button"
                      fillMode={"solid"}
                      themeColor={"primary"}
                      style={{ marginRight: 4 }}
                      onClick={handleFormClose}
                    >
                      Go To List
                    </Button>
                    <ButtonWithLoading
                      label="Save Split"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 4 }}
                      onClick={handleFormClose}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            )}
          </FormElement>
        )}
      />
    </>
  );
};

export default SplitItem;
